import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Film, Tv, Star, Play, X, Sparkles, SearchX, ChevronRight, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import Navbar from "@/components/Navbar";
import MovieCard from "@/components/MovieCard";
import {
  getVodCategories,
  getVodStreams,
  getSeriesCategories,
  getSeries,
  imageUrl,
  proxyStreamUrl,
} from "@/lib/xtream";
import { loadCatalog } from "@/lib/catalog";
import {
  getFavorites,
  getProgressList,
  clearProgress,
  getSearchHistory,
  addSearchHistory,
  removeSearchHistory,
  clearSearchHistory,
} from "@/lib/store";

export default function Browse() {
  const { creds } = useAuth();
  const { openPlayer } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab") || "movies";
  const q = (params.get("q") || "").trim();

  const isList = tab === "mylist";
  const isSearch = tab === "search";
  const isBrowse = !isList && !isSearch;
  const kind = tab === "series" ? "series" : "movie";

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [continueList, setContinueList] = useState([]);

  const [searchData, setSearchData] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setContinueList(getProgressList(creds));
    setHistory(getSearchHistory(creds));
  }, [creds, tab]);

  useEffect(() => {
    let cancelled = false;
    setActiveCat("all");
    setError(null);
    if (!isBrowse) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const fetchAll =
      tab === "series"
        ? Promise.all([getSeriesCategories(creds), getSeries(creds)])
        : Promise.all([getVodCategories(creds), getVodStreams(creds)]);
    fetchAll
      .then(([cats, list]) => {
        if (cancelled) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setItems(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Could not load your library.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tab, creds, isBrowse]);

  useEffect(() => {
    let cancelled = false;
    if (!isSearch) return;
    setSearchLoading(true);
    setError(null);
    loadCatalog(creds)
      .then((data) => !cancelled && setSearchData(data))
      .catch(() => !cancelled && setError("Could not load your library."))
      .finally(() => !cancelled && setSearchLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isSearch, creds]);

  // Record a search term once it settles
  useEffect(() => {
    if (!isSearch || !q) return;
    const id = setTimeout(() => {
      addSearchHistory(creds, q);
      setHistory(getSearchHistory(creds));
    }, 1200);
    return () => clearTimeout(id);
  }, [isSearch, q, creds]);

  const favItems = useMemo(() => (isList ? getFavorites(creds) : []), [creds, isList, tab]);

  const counts = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      map[it.category_id] = (map[it.category_id] || 0) + 1;
    });
    return map;
  }, [items]);

  const filtered = useMemo(() => {
    if (activeCat === "all") return items;
    return items.filter((i) => String(i.category_id) === String(activeCat));
  }, [items, activeCat]);

  const recent = useMemo(() => {
    if (!isBrowse) return [];
    const ts = (it) => Number(it.added || it.last_modified || 0);
    return [...items].sort((a, b) => ts(b) - ts(a)).slice(0, 18);
  }, [items, isBrowse]);

  const genreRows = useMemo(() => {
    if (!isBrowse || activeCat !== "all") return [];
    const byCat = {};
    items.forEach((it) => {
      (byCat[it.category_id] = byCat[it.category_id] || []).push(it);
    });
    return categories
      .map((c) => ({ id: c.category_id, name: c.category_name, list: byCat[c.category_id] || [] }))
      .filter((r) => r.list.length > 0);
  }, [items, categories, isBrowse, activeCat]);

  const searchResults = useMemo(() => {
    if (!searchData || !q) return { movies: [], series: [] };
    const needle = q.toLowerCase();
    return {
      movies: searchData.movies.filter((m) => (m.name || "").toLowerCase().includes(needle)).slice(0, 120),
      series: searchData.series.filter((s) => (s.name || "").toLowerCase().includes(needle)).slice(0, 120),
    };
  }, [searchData, q]);

  const openItem = (item, k) => {
    if (k === "series") navigate(`/series/${item.series_id}`);
    else navigate(`/movie/${item.stream_id}`);
  };

  const resume = (rec) => {
    const url = proxyStreamUrl(creds, rec.kind === "episode" ? "series" : "movie", rec.playableId, rec.ext || "mp4");
    openPlayer({ url, title: rec.name, poster: rec.poster ? imageUrl(rec.poster) : null, meta: rec });
  };

  const removeResume = (rec) => {
    clearProgress(creds, rec.kind, rec.playableId);
    setContinueList(getProgressList(creds));
  };

  return (
    <div className="min-h-screen bg-[#05070A]">
      <Navbar />
      <div className="mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12 py-6">
        {/* Continue watching */}
        {isBrowse && continueList.length > 0 && (
          <div className="mb-10">
            <RowHeading icon={Play} iconClass="text-[#14A3FF] fill-[#14A3FF]">Continue Watching</RowHeading>
            <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-none" data-testid="continue-watching-row">
              {continueList.slice(0, 20).map((rec) => {
                const pct = rec.duration ? Math.min(100, (rec.position / rec.duration) * 100) : 0;
                return (
                  <div
                    key={`${rec.kind}:${rec.playableId}`}
                    className="group relative shrink-0 w-52 cursor-pointer"
                    onClick={() => resume(rec)}
                    data-testid="continue-card"
                  >
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-[#131924] border border-white/5 group-hover:border-[#14A3FF]/50 transition-all">
                      {rec.poster ? (
                        <img src={imageUrl(rec.poster)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-slate-700">
                          <Film className="h-8 w-8" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="h-11 w-11 rounded-full bg-[#14A3FF] flex items-center justify-center">
                          <Play className="h-5 w-5 text-white fill-white" />
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeResume(rec);
                        }}
                        className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white"
                        data-testid="continue-remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                      <div className="absolute bottom-0 inset-x-0 h-1 bg-white/20">
                        <div className="h-full bg-[#14A3FF]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-200 line-clamp-1">{rec.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recently added */}
        {isBrowse && !loading && !error && recent.length > 0 && (
          <div className="mb-10">
            <RowHeading icon={Sparkles} iconClass="text-[#E5A93C]">Recently Added</RowHeading>
            <PosterScroll items={recent} kind={kind} creds={creds} onOpen={openItem} />
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#14A3FF] mb-1">
            {isSearch ? "Search" : isList ? "My List" : tab === "series" ? "TV Series" : "Movies"}
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white font-heading">
            {isSearch
              ? q
                ? `Results for “${q}”`
                : "Search movies & series"
              : isList
              ? "Saved Movies & Series"
              : tab === "series"
              ? "Your Series Library"
              : "Your Movie Library"}
          </h1>
        </div>

        {/* SEARCH MODE */}
        {isSearch ? (
          <>
            {history.length > 0 && (
              <div className="mb-8" data-testid="search-history">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> Recent searches
                  </p>
                  <button
                    onClick={() => {
                      clearSearchHistory(creds);
                      setHistory([]);
                    }}
                    className="text-xs text-slate-500 hover:text-white"
                    data-testid="clear-history-button"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {history.map((h) => (
                    <div
                      key={h}
                      className="group flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-full bg-[#131924] border border-white/5 hover:border-[#14A3FF]/40 transition-colors"
                    >
                      <button
                        data-testid="history-chip"
                        onClick={() => navigate(`/?tab=search&q=${encodeURIComponent(h)}`)}
                        className="text-sm text-slate-200"
                      >
                        {h}
                      </button>
                      <button
                        onClick={() => {
                          removeSearchHistory(creds, h);
                          setHistory(getSearchHistory(creds));
                        }}
                        className="h-5 w-5 rounded-full flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {searchLoading ? (
              <Loader />
            ) : !q ? (
              <div className="py-16 text-center text-slate-500">Start typing in the search bar above.</div>
            ) : searchResults.movies.length === 0 && searchResults.series.length === 0 ? (
              <Empty icon={SearchX} title="No matches found" sub={`Nothing matched “${q}”. Try a different title.`} />
            ) : (
              <div className="space-y-10">
                {searchResults.movies.length > 0 && (
                  <Section title="Movies" count={searchResults.movies.length}>
                    <Grid>
                      {searchResults.movies.map((item, i) => (
                        <MovieCard key={item.stream_id} item={item} kind="movie" index={i} creds={creds} onClick={() => openItem(item, "movie")} />
                      ))}
                    </Grid>
                  </Section>
                )}
                {searchResults.series.length > 0 && (
                  <Section title="Series" count={searchResults.series.length}>
                    <Grid>
                      {searchResults.series.map((item, i) => (
                        <MovieCard key={item.series_id} item={item} kind="series" index={i} creds={creds} onClick={() => openItem(item, "series")} />
                      ))}
                    </Grid>
                  </Section>
                )}
              </div>
            )}
          </>
        ) : isList ? (
          favItems.length === 0 ? (
            <Empty icon={Star} title="Your list is empty" sub="Tap the star on any movie or series to save it here." iconColor="#E5A93C" />
          ) : (
            <Grid>
              {favItems.map((f, i) => (
                <MovieCard key={`${f.kind}:${f.id}`} item={f.item} kind={f.kind} index={i} creds={creds} onClick={() => openItem(f.item, f.kind)} />
              ))}
            </Grid>
          )
        ) : (
          <>
            {categories.length > 0 && !loading && !error && (
              <div className="flex items-center gap-2.5 overflow-x-auto pb-4 mb-4 scrollbar-none">
                <CatPill label="All" count={items.length} active={activeCat === "all"} onClick={() => setActiveCat("all")} />
                {categories.map((c) => (
                  <CatPill
                    key={c.category_id}
                    label={c.category_name}
                    count={counts[c.category_id] || 0}
                    active={String(activeCat) === String(c.category_id)}
                    onClick={() => setActiveCat(c.category_id)}
                  />
                ))}
              </div>
            )}
            {loading ? (
              <Loader />
            ) : error ? (
              <Empty icon={tab === "series" ? Tv : Film} title={error} sub="Please check your panel is online and reachable, then try again." />
            ) : activeCat === "all" ? (
              genreRows.length === 0 ? (
                <div className="py-32 text-center text-slate-500">Nothing here yet.</div>
              ) : (
                <div className="space-y-9">
                  {genreRows.map((row) => (
                    <div key={row.id}>
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg sm:text-xl font-bold text-white font-heading">{row.name}</h2>
                        <button
                          onClick={() => setActiveCat(row.id)}
                          data-testid="genre-see-all"
                          className="flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors"
                        >
                          See all <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                      <PosterScroll items={row.list.slice(0, 15)} kind={kind} creds={creds} onOpen={openItem} />
                    </div>
                  ))}
                </div>
              )
            ) : filtered.length === 0 ? (
              <div className="py-32 text-center text-slate-500">Nothing found here.</div>
            ) : (
              <Grid>
                {filtered.slice(0, 600).map((item, i) => (
                  <MovieCard key={kind === "series" ? item.series_id : item.stream_id} item={item} kind={kind} index={i} creds={creds} onClick={() => openItem(item, kind)} />
                ))}
              </Grid>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const PosterScroll = ({ items, kind, creds, onOpen }) => (
  <div className="flex gap-4 sm:gap-5 overflow-x-auto pb-3 scrollbar-none">
    {items.map((item, i) => (
      <div key={kind === "series" ? item.series_id : item.stream_id} className="shrink-0 w-32 sm:w-40">
        <MovieCard item={item} kind={kind} index={i} creds={creds} onClick={() => onOpen(item, kind)} />
      </div>
    ))}
  </div>
);

const RowHeading = ({ icon: Icon, iconClass, children }) => (
  <h2 className="text-xl sm:text-2xl font-bold text-white font-heading mb-4 flex items-center gap-2">
    <Icon className={`h-5 w-5 ${iconClass}`} /> {children}
  </h2>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">{children}</div>
);

const Section = ({ title, count, children }) => (
  <div>
    <h2 className="text-xl font-bold text-white font-heading mb-4">
      {title} <span className="text-slate-500 text-base font-medium">({count})</span>
    </h2>
    {children}
  </div>
);

const Loader = () => (
  <div className="flex flex-col items-center justify-center py-32 text-slate-500">
    <Loader2 className="h-8 w-8 animate-spin mb-3 text-[#14A3FF]" />
    Loading your library…
  </div>
);

const Empty = ({ icon: Icon, title, sub, iconColor = "#14A3FF" }) => (
  <div className="flex flex-col items-center justify-center py-32 text-center">
    <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${iconColor}1a` }}>
      <Icon className="h-7 w-7" style={{ color: iconColor }} />
    </div>
    <p className="text-white font-semibold mb-1">{title}</p>
    <p className="text-slate-500 text-sm max-w-md">{sub}</p>
  </div>
);

const CatPill = ({ label, count, active, onClick }) => (
  <button
    data-testid="category-pill-button"
    onClick={onClick}
    className={`shrink-0 flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
      active
        ? "bg-[#14A3FF] text-white shadow-[0_0_18px_rgba(20,163,255,0.4)]"
        : "bg-[#131924] text-slate-300 hover:bg-white/10 border border-white/5"
    }`}
  >
    {label}
    <span className={`text-[11px] ${active ? "text-white/80" : "text-slate-500"}`}>{count}</span>
  </button>
);
